import {getHopnPolicies} from "@/utils/functions.js";
import PolicyPopupContent from "@/customComponents/sharedComponents/PolicyPopupContent.jsx";
import {LatenessPoliciesContent} from "./latenessPolicies.jsx";
import {arrowRightSlim, smallArrowRight, logoLatest} from "@img/index.js";
import performanceIcon from "@/assets/images/performance.svg";
import refundIcon from "@/assets/images/refund.svg";
import clockJourIcon from "@/assets/images/clock-jour.svg";
import goodShildIcon from "@/assets/images/good-shild.svg";
import cancelChangeIcon from "@/assets/images/cancel-chnage.svg";
import dollerClicklIcon from "@/assets/images/doller-clickl.svg";

/**
 * Single source for the "Policies — For A Smooth Journey" content, shared by:
 *  - the booking-flow modal steps (BookRideSteps/modalPages/*) — values from
 *    /trips/calculate-policies via bookingPolicyValues()
 *  - the trip-page PoliciesPopup (details / edit / confirm pages) — values from
 *    the GET-trip response's policy_popup via tripPolicyValues()
 *
 * Content components take a `policy` object of RAW values (same idea as the
 * flow FAQs in constants/faqs.js) so each surface plugs in its own source
 * (builders live in ./policyValues.js), and a `spacing` flag: the booking
 * modal wants the `modalPages-spacing pb-8` gutters, the standalone Modal
 * already pads its body.
 */

const POLICY_IMAGE_URLS = {
    'performance.svg': performanceIcon,
    'refund.svg': refundIcon,
    'clock-jour.svg': clockJourIcon,
    'good-shild.svg': goodShildIcon,
    'cancel-chnage.svg': cancelChangeIcon,
    'doller-clickl.svg': dollerClicklIcon,
};

const gutters = (spacing) => (spacing ? ' modalPages-spacing pb-8' : '');

export function LiabilityDisclaimerContent() {
    return (
        <div className="hopn-disclaimer-box">
            <h2>Liability Disclaimer</h2>
            <p>Hop'n is not responsible for any losses, damages, or inconveniences caused by a missed pickup, including but not limited to:</p>
            <ul>
                <li>Missed flights, train, or bus connections</li>
                <li>Hotel cancellations or additional lodging expenses</li>
                <li>Business meeting delays or missed appointments</li>
                <li>Any direct or indirect financial or personal losses due to ride unavailability</li>
            </ul>
            <p>The recommended pickup time provided is only a suggestion and is not guaranteed, as actual travel time depends on traffic conditions, road closures, weather, and other factors beyond our control. Passengers are responsible for planning accordingly and allowing extra buffer time.</p>
            <p>Hop'n is also not liable for any delays, missed flights, or other consequences arising during the ride itself, including delays caused by traffic, road conditions, or other uncontrollable circumstances.</p>
            <p>By using Hop'n services, passengers acknowledge and accept that Hop'n is not liable for any claims, losses, or damages resulting from uncontrollable pickup failures, inaccurate time recommendations, or delays occurring during the ride.</p>
        </div>
    );
}

// The "For A Smooth Journey" listing. onOpenStep receives the page keys from
// getHopnPolicies() (passengerPolicies, refundPolicies, ...) plus
// "policiesExtraDetails" for the More link — the booking flow uses them as step
// names, PoliciesPopup as its internal view keys.
export function PoliciesListingContent({onOpenStep, spacing = true}) {
    const HopnPolicies = getHopnPolicies();
    return (
        <div className={`hopn-policies-disclaimer${gutters(spacing)}`}>
            <h2><span>Policies</span>For A Smooth Journey</h2>
            <div className="hopn-policies-grid">
                {HopnPolicies.map((values) => {
                    const iconUrl = POLICY_IMAGE_URLS[values.image_name];
                    return (
                        <div className="hopn-policy-item" key={values.page_name}>
                            <button onClick={() => onOpenStep(values.page_name)}>
                                <span className="hopn-policy-item-icon"><img decoding="async" src={iconUrl} alt={values.image_name}/></span>
                                <div className="hopn-policy-item-inner">
                                    <span className="hopn-policy-item-text">{values.title}</span>
                                    <span className="hopn-policy-item-arrow"><img decoding="async" src={arrowRightSlim} alt="Arrow right"/></span>
                                </div>
                            </button>
                        </div>
                    );
                })}
            </div>
            <div className="more-details text-right">
                <button onClick={() => onOpenStep("policiesExtraDetails")} className="inline-flex items-center">More <img src={smallArrowRight} alt="Arrow right"/></button>
            </div>
            <LiabilityDisclaimerContent/>
        </div>
    );
}

// Both lateness policies now live in ONE tabbed component (see latenessPolicies.jsx)
// — these two entries only differ by which tab opens first. Kept as separate exports
// so the booking steps, the policies listing and PoliciesPopup keep their wiring.
export function PassengerPoliciesContent({policy = {}, spacing = true}) {
    return <LatenessPoliciesContent initialTab="passenger" policy={policy} spacing={spacing}/>;
}

export function DriverPoliciesContent({policy = {}, spacing = true}) {
    return <LatenessPoliciesContent initialTab="driver" policy={policy} spacing={spacing}/>;
}

export function RefundPoliciesContent({spacing = true}) {
    return (
        <div className={`hopn-instruction-layout${gutters(spacing)}`}>
            <span className="spacial-layout"><h6>3 hours before pickup: </h6><p>Full refund to your original payment method.</p></span>
            <span className="spacial-layout"><h6>After 3 hours of pickup: </h6><p>Receive 100 % <img src={logoLatest} alt="hop'n" style={{maxWidth: '40px', display: 'inline-block', verticalAlign: 'sub', margin: '0 5px'}}/>credit valid for 36 months.</p></span>
            <p>Refunds typically appear within 1–3 business days, depending on your bank</p>
            <ul><li>Full refund if your ride is canceled by Hop'n due to unforeseen circumstances</li></ul>
            <p>By using Hop'n, you agree to this refund policy.</p>
        </div>
    );
}

export function PickupPoliciesContent({spacing = true}) {
    return (
        <div className={`hopn-instruction-layout${gutters(spacing)}`}>
            <h3>Hop'n Guaranteed Pickup Policy <span>At Hop'n, we are committed to providing reliable, on-time pickups with no cancellations from our end. However, certain uncontrollable factors may occasionally affect service availability</span></h3>
            <span className="spacial-layout"><h6>Exceptions to Guaranteed Pickup</h6><p>Hop'n is not responsible for any losses, damages, or inconveniences caused by a missed pickup, including but not limited to:</p></span>
            <ol>
                <li>Severe Weather Conditions – Extreme weather (e.g., snowstorms, hurricanes, flooding, heavy fog) that makes travel unsafe.</li>
                <li>Road Closures &amp; Traffic Disruptions – Unexpected roadblocks, accidents, construction, protests, or government-imposed travel restrictions.</li>
                <li>Natural Disasters &amp; Emergencies – Earthquakes, wildfires, floods, or other disasters affecting travel routes.</li>
                <li>Airport &amp; Security Restrictions – Access limitations imposed by airports, security lockdowns, or unexpected policy changes.</li>
                <li>Mechanical Issues &amp; Vehicle Breakdowns – In the event of a vehicle breakdown or unexpected mechanical failure, Hop'n will attempt to provide a replacement ride, but service delays or cancellations may occur.</li>
                <li>Driver Health &amp; Safety Concerns – If a driver experiences sudden illness, injury, or a medical emergency, alternative arrangements may be provided when possible.</li>
                <li>Customer No-Shows &amp; Last-Minute Cancellations – If a customer fails to appear within the designated wait time or cancels on short notice, standard cancellation policies will apply.</li>
                <li>Public Health Emergencies – Epidemics, pandemics, or official public health orders restricting transportation services.</li>
                <li>Legal or Law Enforcement Orders – If a ride cannot be completed due to legal restrictions, law enforcement directives, or driver detainment, Hop'n may not be able to provide service.</li>
                <li>Unforeseen Operational Disruptions – If technical issues, system failures, or force majeure events (e.g., cyberattacks, power outages) affect Hop'n's ability to operate, temporary service disruptions may occur.</li>
            </ol>
            <span className="spacial-layout"><h6>Compensation Policy for Uncontrollable Cancellations <span>In the rare case that Hop'n must cancel your ride due to an uncontrollable event, we will:</span></h6></span>
            <ul>
                <li>Missed flights, train, or bus connections</li>
                <li>Hotel cancellations or additional lodging expenses</li>
                <li>Business meeting delays or missed appointments</li>
                <li>Any direct or indirect financial or personal losses due to ride unavailability</li>
            </ul>
            <div className="important-note">By using Hop'n services, passengers acknowledge and accept that Hop'n is not liable for any claims, losses, or damages resulting from uncontrollable pickup failures</div>
            <span className="spacial-layout"><h6>Our Commitment to You</h6></span>
            <p>At Hop'n, we value your trust and strive to minimize disruptions. We will always communicate transparently and ensure that any cancellations due to unforeseen circumstances are handled fairly.</p>
        </div>
    );
}

export function TollPoliciesContent({spacing = true}) {
    return (
        <div className={`hopn-instruction-layout${gutters(spacing)}`}>
            <h3>Hop'n Toll Charges Policy <span>Hop'n ensures transparent pricing for all rides. If a toll road is used during your trip, toll charges will be applied as follows:</span></h3>
            <span className="spacial-layout"><h6>When Toll Charges Apply:</h6></span>
            <ul>
                <li>Passenger-Requested Toll Route – If a passenger chooses a toll road, the toll fee will be added to the final fare</li>
                <li>Driver-Suggested Toll Route – If a driver recommends a toll road, the passenger must approve before toll charges apply</li>
            </ul>
            <span className="spacial-layout"><h6>How Toll Charges Are Processed:</h6></span>
            <ul>
                <li>Toll fees are added to the final trip cost and charged to the passenger's payment method.</li>
                <li>The total fare, including tolls, will be processed after the ride is completed.</li>
            </ul>
            <div className="important-note">By using Hop'n, you agree to this refund policy.</div>
        </div>
    );
}

// Cancellation & Change — the only fully dynamic page. Headings are static,
// sentences come from the policy source; contact-us empty state when nothing
// is loaded (never fabricates policy copy).
export function CancellationPoliciesContent({policy = {}}) {
    return (
        <PolicyPopupContent
            cancellationText={policy.cancellationText}
            modificationText={policy.modificationText}
            error={policy.loaded ? null : 'empty'}
        />
    );
}

export function PoliciesExtraDetailsContent({spacing = true}) {
    return (
        <div className={`hopn-policies-disclaimer detail-one${gutters(spacing)}`}>
            <h2>TERMS &amp; CONDITIONS</h2>
            <p>8258031 Canada Inc.</p>
            <p>Last Updated: January 2026</p>
            <div className="hopn-disclaimer-box center">
                These Passenger Terms &amp; Conditions ("Terms") govern your access to and use of the website, mobile application, and services (collectively, the "Platform") operated by 8258031 Canada Inc. ("Company", "we", "us", "our"). PLEASE READ THESE TERMS CAREFULLY BEFORE BOOKING OR USING OUR SERVICES.
            </div>
            <ol>
                <li>
                    <strong>ACCEPTANCE OF TERMS</strong>
                    <ul>
                        <li>By accessing, browsing, or using the Platform, or by completing a booking, you acknowledge that you have read, understood, and agree to be bound by these Terms.</li>
                        <li>If you do not agree to these Terms, you must not access or use the Platform or services.</li>
                        <li>Your acceptance constitutes a legally binding agreement between you and 8258031 Canada Inc.</li>
                    </ul>
                </li>
                <li>
                    <strong>CHANGES TO THESE TERMS</strong>
                    <p>We may update or modify these Terms from time to time to reflect changes in our services, operational practices, pricing, safety requirements, or applicable legal obligations. When changes are made:</p>
                    <ul>
                        <li>The "Last Updated" date will be revised; and The updated Terms will be posted on the Platform.</li>
                        <li>Your continued use of the Platform after updated Terms are posted constitutes acceptance of the revised Terms.</li>
                        <li>If a change is material, additional notice may be provided via the Platform, email, or SMS where required by applicable law.</li>
                    </ul>
                </li>
                <li>
                    <strong>ELIGIBILITY</strong>
                    <p>To use the Platform, you must:</p>
                    <ul>
                        <li>Be at least 18 years of age (or the age of majority in your jurisdiction); and Have the legal capacity to enter into a binding contract.</li>
                    </ul>
                </li>
                <li>
                    <strong>ACCOUNT REGISTRATION</strong>
                    <p>An account is required to book services through the Platform. You agree that:</p>
                    <ul>
                        <li>All information provided is accurate, complete, and current;</li>
                        <li>You are responsible for maintaining the confidentiality of your login credentials; and</li>
                        <li>You are responsible for all activity conducted through your account.</li>
                    </ul>
                    <p>The Company reserves the right to suspend or terminate accounts for false information, misuse, security concerns, or violations of these Terms.</p>
                </li>
                <li>
                    <strong>NATURE OF SERVICES</strong>
                    <p>The Company operates as a transportation services arranger and booking platform, facilitating pre-arranged transportation services provided by independent, licensed drivers. Drivers are independent service providers and are not employees, agents, or representatives of the Company.</p>
                </li>
                <li>
                    <strong>SERVICE SCOPE</strong>
                    <p>Services are pre-arranged and scheduled and are assigned based on vehicle availability, passenger count, luggage, and booking details provided by the passenger.</p>
                    <ul>
                        <li>
                            <strong>Airport Pickups</strong>
                            <ul>
                                <li>Pre-arranged bookings only</li>
                                <li>Unlimited waiting time included</li>
                                <li>Real-time flight tracking may be used to adjust pickup timing</li>
                                <li>Meet-and-greet service where applicable</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Airport Drop-Offs</strong>
                            <ul>
                                <li>Scheduled pickups based on passenger-selected time</li>
                                <li>Navigation-assisted route planning</li>
                            </ul>
                        </li>
                    </ul>
                </li>
                <li>
                    <strong>PASSENGER &amp; LUGGAGE ACCURACY</strong>
                    <p>All bookings are driver- and vehicle-dependent and assigned strictly based on the information provided at the time of booking.</p>
                    <p>Passengers are solely responsible for ensuring accuracy of:</p>
                    <ul>
                        <li>Number of passengers;</li>
                        <li>Luggage size and quantity; and</li>
                        <li>Oversized or special items (including strollers, child seats, golf bags, skis, or wheelchairs).</li>
                    </ul>
                </li>
                <li>
                    <strong>FARES &amp; PRICING</strong>
                    <p>All fares are flat-rate and confirmed at the time of booking. Pricing is displayed as Base Price + applicable fees – discounts = Final Payable Amount. Prices are exclusive of tax and gratuity unless stated otherwise.</p>
                    <p>Fare changes may occur only if:</p>
                    <ul>
                        <li>The passenger modifies booking details;</li>
                        <li>Incorrect booking information was provided; or</li>
                        <li>Force majeure circumstances apply.</li>
                    </ul>
                    <p>Traffic conditions, peak hours, or demand do not alter confirmed flat-rate fares.</p>
                </li>
                <li>
                    <strong>PAYMENT</strong>
                    <ul>
                        <li>All payments must be completed through the Platform only;</li>
                        <li>Cash payments to drivers are not permitted; and</li>
                        <li>Payments are processed using secure third-party payment processors.</li>
                    </ul>
                </li>
                <li>
                    <strong>CHILD SEATS</strong>
                    <p>Child seats are provided upon request only and as a courtesy. Passengers are responsible for selecting the appropriate seat type. Drivers may assist with installation; however, final responsibility rests with the parent or legal guardian.</p>
                </li>
                <li>
                    <strong>PASSENGER CONDUCT</strong>
                    <ul>
                        <li>Wear seatbelts at all times; and</li>
                        <li>Behave respectfully toward drivers and property.</li>
                    </ul>
                    <p>Smoking, vaping, alcohol consumption, abusive behaviour, or creating excessive mess is strictly prohibited and may result in immediate termination of service without refund.</p>
                </li>
                <li>
                    <strong>VEHICLE DAMAGE, MESS &amp; CLEANING FEES</strong>
                    <p>Passengers are fully responsible for any damage, excessive mess, or contamination caused by themselves or accompanying persons.</p>
                    <ul>
                        <li>
                            <strong>Child Seat Damage</strong>
                            <p>If a child seat is contaminated, damaged, or rendered unsafe, it may be discarded for safety reasons, and the passenger will be charged the full replacement cost.</p>
                        </li>
                        <li>
                            <strong>Vomiting &amp; Biohazard Cleaning</strong>
                            <ul>
                                <li>Mandatory deep cleaning and sanitization is required;</li>
                                <li>A minimum cleaning fee of $150 CAD applies; and</li>
                                <li>Charges may exceed $150 if extensive cleaning or repairs are required.</li>
                            </ul>
                        </li>
                    </ul>
                </li>
                <li>
                    <strong>MISSED FLIGHTS &amp; DELAYS</strong>
                    <p>Except where caused by the Company's gross negligence or wilful misconduct, the Company is not liable for missed flights or delays resulting from traffic, weather, road conditions, passenger lateness, or force majeure events.</p>
                </li>
                <li>
                    <strong>PERSONAL BELONGINGS</strong>
                    <p>Passengers are responsible for their personal belongings. The Company is not liable for lost or damaged items but may make reasonable efforts to assist with recovery.</p>
                </li>
                <li>
                    <strong>OFF-PLATFORM DEALINGS</strong>
                    <p>All bookings, communications, and payments must occur through the Platform. The Company assumes no liability for any arrangements made outside the Platform.</p>
                </li>
                <li>
                    <strong>SAFETY &amp; SOS FEATURES</strong>
                    <p>Safety features may facilitate emergency contact and location sharing but do not replace emergency services.</p>
                </li>
                <li>
                    <strong>LIMITATION OF LIABILITY</strong>
                    <p>Services are provided "as is" and "as available." To the maximum extent permitted by law, the Company is not liable for indirect, incidental, or consequential damages.</p>
                </li>
                <li>
                    <strong>FORCE MAJEURE</strong>
                    <p>The Company is not liable for service failure due to events beyond its reasonable control, including natural disasters, labour disruptions, government actions, or infrastructure failures.</p>
                </li>
                <li>
                    <strong>TERMINATION</strong>
                    <p>The Company may suspend or terminate accounts for violations of these Terms. Users may request account termination at any time. Outstanding obligations survive termination.</p>
                </li>
                <li>
                    <strong>GOVERNING LAW</strong>
                    <p>These Terms are governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein.</p>
                </li>
                <li>
                    <strong>CONTACT</strong>
                    <p>For questions or support, please contact customer service through the Platform.</p>
                </li>
            </ol>
        </div>
    );
}
